<template>
  <div>
    <v-dialog
      v-model="dialog"
      :max-width="options.width"
      :style="{ zIndex: options.zIndex }"
      @keydown.esc="cancel"
    >
      <v-card>
        <div
          :class="{
            'd-flex': true,
            'flex-row': !mobile,
            'px-10': !mobile,
            'pt-8': !mobile,
            'mb-0': !mobile,
            'px-6': mobile,
            'pt-4': mobile
          }"
        >
          <p class="subtitle-1 font-weight-bold">Create combined payment</p>
          <v-tooltip right max-width="400">
            <template v-slot:activator="{ on }">
              <v-icon
                small
                :class="{ 'ml-1': !mobile, 'ml-2': mobile }"
                v-on="on"
                >mdi-help-circle</v-icon
              >
            </template>
            <p class="white--text">
              Split the tenant's payment among multiple transactions
              chronologically
            </p>
          </v-tooltip>
        </div>
        <v-stepper v-model="e1" :class="{ 'px-4': !mobile }">
          <v-stepper-header>
            <v-stepper-step :complete="e1 > 1" step="1"
              >Enter amount</v-stepper-step
            >
            <v-divider></v-divider>
            <v-stepper-step :complete="e1 > 2" step="2"
              >Confirm payments</v-stepper-step
            >
            <v-divider></v-divider>
            <v-stepper-step :complete="e1 > 3" step="2"
              >Add details</v-stepper-step
            >
          </v-stepper-header>
          <v-stepper-items>
            <v-stepper-content step="1" class="pt-0">
              <v-currency-field
                v-model="amount"
                type="number"
                label="Amount"
                dense
                outlined
                class="my-1"
                hide-details
              >
                <template v-slot:prepend-inner>
                  <v-icon small class="mt-1">mdi-cash</v-icon>
                </template>
              </v-currency-field>
              <div style="width: 100%" class="d-flex flex-row-reverse">
                <v-switch
                  v-model="useCredits"
                  :class="{ 'mx-4': !mobile }"
                  :disabled="!hasCredits"
                >
                  <template #label> Use Credits </template>
                </v-switch>
              </div>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  color="primary darken-1"
                  depressed
                  @click="next(2)"
                  @keydown.enter="next(2)"
                  >Next</v-btn
                >
                <v-btn color="grey darken-1" text @click="cancel">Cancel</v-btn>
              </v-card-actions>
            </v-stepper-content>
            <v-stepper-content step="2" class="pt-0">
              <v-data-table
                fixed-header
                :items="items"
                :headers="headers"
                :height="!mobile ? 300 : 350"
                disable-pagination
                disable-filtering
                disable-sort
                hide-default-footer
                dense
              >
                <template v-slot:item="{ item }">
                  <tr v-if="!mobile">
                    <td>
                      <p class="caption">{{ item.refNo }}</p>
                    </td>
                    <td>
                      <p class="caption">{{ parseCategory(item.category) }}</p>
                    </td>
                    <td>
                      <p class="caption">
                        {{ item.startDate }} to {{ item.endDate }}
                      </p>
                    </td>
                    <td>
                      <p class="caption">{{ item.dueDate }}</p>
                    </td>
                    <td>
                      <p class="caption">{{ parseStatus(item.status) }}</p>
                    </td>
                    <td>
                      <p class="caption">{{ formatCurrency(item.total) }}</p>
                    </td>
                    <td>
                      <p v-if="item.balanceUsed" class="caption">
                        {{ formatCurrency(item.amountPaid) }} +
                        {{ formatCurrency(item.balanceUsed) }} credits
                      </p>
                      <p v-else class="caption">
                        {{ formatCurrency(item.amountPaid) }}
                      </p>
                    </td>
                    <td>
                      <div class="d-flex flex-row">
                        <p class="caption font-weight-bold">
                          {{ formatCurrency(item.amountToPay) }}
                        </p>
                        <v-tooltip
                          v-if="
                            item.amountToPay +
                              item.amountPaid +
                              item.balanceUsed >
                            item.total
                          "
                          bottom
                        >
                          <template v-slot:activator="{ on }">
                            <v-icon
                              color="warning"
                              class="ml-1"
                              x-small
                              v-on="on"
                              >mdi-alert-circle</v-icon
                            >
                          </template>
                          <p class="white--text">
                            Overpayment of
                            {{
                              formatCurrency(
                                item.total -
                                  item.amountToPay -
                                  item.amountPaid -
                                  item.balanceUsed
                              )
                            }}
                          </p>
                        </v-tooltip>
                        <v-tooltip
                          v-if="
                            item.amountToPay +
                              item.amountPaid +
                              item.balanceUsed <
                            item.total
                          "
                          bottom
                        >
                          <template v-slot:activator="{ on }">
                            <v-icon
                              color="warning"
                              class="ml-1"
                              x-small
                              v-on="on"
                              >mdi-alert-circle</v-icon
                            >
                          </template>
                          <p class="white--text">
                            Underpayment of
                            {{
                              formatCurrency(
                                item.total -
                                  item.amountToPay -
                                  item.amountPaid -
                                  item.balanceUsed
                              )
                            }}
                          </p>
                        </v-tooltip>
                      </div>
                    </td>
                    <td>
                      <v-file-input
                        v-model="item.newAttachments"
                        accept="image/*, application/pdf"
                        chips
                        small-chips
                        multiple
                        dense
                        hide-details
                        truncate-length="5"
                        hide-input
                      >
                      </v-file-input>
                      <p class="caption">
                        {{ item.newAttachments.length }} file(s)
                      </p>
                    </td>
                  </tr>
                  <tr v-else>
                    <v-divider></v-divider>
                    <div class="d-flex flex-column py-2">
                      <div class="d-flex flex-row">
                        <v-col class="pa-0">
                          <p class="font-weight-bold body-2">
                            {{ parseCategory(item.category) }}
                          </p>
                        </v-col>
                        <v-col class="pa-0" align="end">
                          <p class="caption">No. {{ item.refNo }}</p>
                        </v-col>
                      </div>
                      <div class="d-flex flex-row">
                        <v-col class="pa-0">
                          <p class="caption">Status</p>
                        </v-col>
                        <v-col class="pa-0" align="end">
                          <p class="caption">{{ parseStatus(item.status) }}</p>
                        </v-col>
                      </div>
                      <div class="d-flex flex-row">
                        <v-col class="pa-0">
                          <p class="caption">Total</p>
                        </v-col>
                        <v-col class="pa-0" align="end">
                          <p class="caption">
                            {{ formatCurrency(item.total) }}
                          </p>
                        </v-col>
                      </div>
                      <div class="d-flex flex-row">
                        <v-col class="pa-0">
                          <p class="caption">Prev. Paid</p>
                        </v-col>
                        <v-col class="pa-0" align="end">
                          <p class="caption"></p>
                          <p v-if="item.balanceUsed" class="caption">
                            {{ formatCurrency(item.amountPaid) }} +
                            {{ formatCurrency(item.balanceUsed) }} credits
                          </p>
                          <p v-else class="caption">
                            {{ formatCurrency(item.amountPaid) }}
                          </p>
                        </v-col>
                      </div>
                      <div class="d-flex flex-row">
                        <v-col class="pa-0">
                          <p class="caption">Amount To Pay</p>
                        </v-col>
                        <v-col class="pa-0" align="end">
                          <p class="caption font-weight-bold">
                            {{ formatCurrency(item.amountToPay) }}
                          </p>
                        </v-col>
                      </div>
                      <div class="d-flex flex-row">
                        <v-col class="pa-0">
                          <p class="caption">{{ item.period }}</p>
                        </v-col>
                      </div>
                    </div>
                  </tr>
                </template>
                <template v-slot:body.append="{ headers }">
                  <tr v-if="!mobile">
                    <td :colspan="headers.length - 4"></td>
                    <td>
                      <p class="font-weight-bold">Total</p>
                    </td>
                    <td></td>
                    <td>
                      <p class="body-1 font-weight-bold">
                        {{ formatCurrency(total) }}
                      </p>
                    </td>
                    <td></td>
                  </tr>
                  <tr v-else>
                    <div class="d-flex flex-column py-2">
                      <div class="d-flex flex-row">
                        <v-col class="pa-0">
                          <p class="font-weight-bold body-2">TOTAL</p>
                        </v-col>
                        <v-col class="pa-0" align="end">
                          <p class="font-weight-bold body-2">
                            {{ formatCurrency(total) }}
                          </p>
                        </v-col>
                      </div>
                    </div>
                  </tr>
                </template>
              </v-data-table>
              <v-divider></v-divider>
              <div style="width: 100%">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-row>
                      <v-col class="pt-0" cols="12" md="8">
                        <v-menu
                          v-model="dateMenu"
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y
                          min-width="290px"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="date"
                              label="Date Paid"
                              prepend-inner-icon="mdi-calendar-month"
                              v-bind="attrs"
                              readonly
                              outlined
                              hide-details
                              dense
                              v-on="on"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="date"
                            no-title
                            scrollable
                            @input="dateMenu = false"
                          >
                          </v-date-picker>
                        </v-menu>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col class="py-0" cols="12" md="12">
                        <v-checkbox
                          v-model="confirmSendEmail"
                          class="mt-0 ml-2"
                          hide-details
                        >
                          <template v-slot:label>
                            Send email to tenant
                          </template>
                        </v-checkbox>
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-row>
                      <v-col class="py-0" cols="12" md="12">
                        <div class="d-flex flex-row justify-space-between">
                          <p class="caption">Subtotal</p>
                          <p class="caption">
                            {{ formatCurrency(total) }}
                          </p>
                        </div>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col class="py-0" cols="12" md="12">
                        <div class="d-flex flex-row justify-space-between">
                          <p class="caption">Credits Used</p>
                          <p class="caption">
                            {{ formatCurrency(creditsUsed) }}
                          </p>
                        </div>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" md="12">
                        <div class="d-flex flex-row justify-space-between">
                          <p>Total Paid</p>
                          <p class="font-weight-bold">
                            {{ formatCurrency(totalPaid) }}
                          </p>
                        </div>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </div>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn :small="mobile" text @click="back">Back</v-btn>
                <v-btn
                  :small="mobile"
                  color="primary darken-1"
                  text
                  :disabled="!items.length"
                  @click="finish"
                  >Finish</v-btn
                >
                <v-btn
                  :small="mobile"
                  color="primary darken-1"
                  :disabled="!items.length"
                  depressed
                  @click="next(3)"
                  @keydown.enter="next(3)"
                  >Add details</v-btn
                >
                <v-btn
                  :small="mobile"
                  color="grey darken-1"
                  text
                  @click="cancel"
                  >Cancel</v-btn
                >
              </v-card-actions>
            </v-stepper-content>
            <v-stepper-content step="3" class="pt-0">
              <v-textarea
                v-model="description"
                label="(Optional) Add a description"
                dense
                outlined
                class="my-1"
                hide-details
              >
              </v-textarea>
              <v-file-input
                v-model="attachments"
                accept="image/*, application/pdf"
                label="Upload Attachments"
                prepend-icon=""
                chips
                small-chips
                multiple
                outlined
                dense
              ></v-file-input>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn :small="mobile" text @click="back">Back</v-btn>
                <v-btn color="primary darken-1" text @click="finish"
                  >Finish</v-btn
                >
                <v-btn color="grey darken-1" text @click="cancel">Cancel</v-btn>
              </v-card-actions>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { dayjs } from '../../util/date'

export default {
  data() {
    return {
      options: {
        color: 'primary',
        width: 700,
        zIndex: 200
      },
      loading: false,
      dialog: false,
      e1: 1,
      amount: 0,
      items: [],
      bedDetails: null,
      headers: [
        { text: '#', value: 'refNo' },
        { text: 'Category', value: 'category' },
        { text: 'Period' },
        { text: 'Due Date', value: 'dueDate' },
        { text: 'Status', value: 'status' },
        { text: 'Total' },
        { text: 'Prev. Paid' },
        { text: 'Amount To Pay' },
        { text: 'Attach Files' }
      ],
      total: 0,
      confirmSendEmail: false,
      useCredits: false,
      hasCredits: false,
      lease: {},
      date: null,
      dateMenu: false,
      description: '',
      attachments: []
    }
  },
  computed: {
    totalPaid() {
      return this.total - this.creditsUsed
    },
    mobile() {
      return this.$vuetify.breakpoint.xs
    },
    creditsUsed() {
      return this.useCredits
        ? this.total >= this.bedDetails.balance
          ? this.bedDetails.balance
          : this.total
        : 0
    },
    property() {
      return this.$store.getters['properties/getProperty']
    },
    categories() {
      return this.property.payment_categories_details[0].categories
    }
  },
  methods: {
    open(params) {
      this.options.width = 700
      this.bedDetails = params.bedDetails
      this.dialog = true
      this.hasCredits = this.bedDetails.balance > 0
      this.useCredits = this.hasCredits
      this.confirmSendEmail = false
      this.date = dayjs().format('YYYY-MM-DD')
      this.dateMenu = false

      return new Promise((resolve, reject) => {
        this.resolve = resolve
        this.reject = reject
      })
    },
    async next(step) {
      switch (step) {
        case 2:
          this.options.width = 1000
          this.e1 = 2
          this.$root.$setLoading(true)
          if (this.bedDetails.leaseId != null) {
            this.items = (
              await this.$store.dispatch('payments/fetchCalculatedPayments', {
                amount:
                  this.amount +
                  (this.useCredits ? Math.max(this.bedDetails.balance, 0) : 0),
                leaseId: this.bedDetails.leaseId
              })
            ).map((item) => {
              item.newAttachments = []
              item.startDate = dayjs(item.startDate).format('YYYY-MM-DD')
              item.endDate = dayjs(item.endDate).format('YYYY-MM-DD')
              item.dueDate = dayjs(item.dueDate).format('YYYY-MM-DD')
              return item
            })
          } else {
            this.items = (
              await this.$store.dispatch(
                'payments/fetchCombinedPaymentsTenant',
                {
                  amount:
                    this.amount +
                    (this.useCredits
                      ? Math.max(this.bedDetails.balance, 0)
                      : 0),
                  tenantId: this.bedDetails.tenantId
                }
              )
            ).map((item) => {
              item.newAttachments = []
              item.startDate = dayjs(item.startDate).format('YYYY-MM-DD')
              item.endDate = dayjs(item.endDate).format('YYYY-MM-DD')
              item.dueDate = dayjs(item.dueDate).format('YYYY-MM-DD')
              return item
            })
          }
          this.total = this.items.reduce((a, b) => a + b.amountToPay, 0)
          this.$root.$setLoading(false)
          break
        case 3:
          this.e1 = 3
      }
    },
    formatCurrency(value) {
      if (value == null) return ''
      return parseFloat(value / 100).toLocaleString('en-PH', {
        style: 'currency',
        currency: 'PHP'
      })
    },
    cancel() {
      this.options.width = 700
      this.e1 = 1
      this.amount = 0
      this.resolve(false)
      this.dialog = false
    },
    back() {
      this.e1 -= 1
      if (this.e1 === 1) this.options.width = 700
    },
    async finish() {
      if (
        await this.$root.$confirm({
          title: 'Create combined payment',
          message: 'Are you sure you would like to submit the payments?'
        })
      ) {
        this.$root.$setLoading(true)
        await this.$store.dispatch('payments/createCombinedPayments', {
          totalAmountPaid: this.totalPaid,
          balanceUsed: this.creditsUsed || 0,
          payments: this.items.map((item) => {
            return {
              paymentId: item.paymentId,
              attachments: item.newAttachments.filter(
                (attachment) => attachment instanceof File
              ),
              currentAttachments: item.attachments
            }
          }),
          tenantId: this.bedDetails.tenantId,
          leaseId: this.bedDetails.leaseId,
          confirmSendEmail: this.confirmSendEmail,
          datePaid: dayjs(this.date).format(),
          description: this.description,
          attachments: this.attachments
        })

        await this.$root
          .$confirm({
            title: 'Create combined payment',
            message: `The payments have been created.`,
            reconfirmOnly: true,
            confirmText: 'OK',
            options: {
              color: 'success'
            }
          })
          .then(() => {
            this.$root.$setLoading(false)
            this.dialog = false
            this.e1 = 1
            this.amount = 0
            this.items = []
            this.total = 0
            // this.bedDetails = null
            this.resolve(true)
          })
      }
    },
    parseStatus(status) {
      const statuses = {
        pending: 'Pending',
        paid_partial: 'Partial Payment'
      }
      return statuses[status] || status
    },
    parseCategory(category) {
      const paymentCategory = this.categories.find(
        ({ value }) => value === category
      )
      return (paymentCategory && paymentCategory.text) || category
    }
  }
}
</script>

<style scoped>
.v-stepper__step {
  padding-top: 14px;
  padding-bottom: 14px;
}
.v-stepper__header,
.v-stepper,
.v-expansion-panel::before {
  box-shadow: none;
}
</style>
